import React, { useEffect, useRef, useState } from 'react'
import { Box, Chip, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import { getOperationUsagesByCode, readOperationLibraryError } from './operationLibraryApi'
import { versionLabelOf } from './libraryRowLines'

/**
 * OperationUsagesDialog — story 184 (CU2 · CA3, CA4, CA5). The read-only dialog that "Used in N workflows" of an
 * operation row opens: in which workflows, and with which version, a library operation is used. It is mounted
 * by the SECTION, never inside a row (R1: React events cross portals, so a dialog inside the row would choose
 * the row on every click and could start a drag).
 *
 * Every opening asks N17 (`GET operationLibrary/{code}/usages`, contrato-sun.md §1) again — ONE request per
 * opening, never one per version (RN-03.2). A late answer of a previous opening is ignored (generation guard).
 * The report is painted as Sun gives it: versions DESC, usages in Sun's order, one row per node (RN-03.3,
 * RN-03.6). Jupiter reads `isLatestWorkflowVersion`; the duplicated `latestWorkflowVersion` Sun also publishes
 * is ignored (contrato-sun.md §7).
 *
 * Props: `open`, `code`, `name` (title = `code · name`, or the code alone), `selectedVersionNumber` (the version
 * the row offers for dragging — highlighted), `selectedVersionLabel` (i18n key of the highlighted block's chip; the
 * panel keeps the default 'Selected version', the OPERATIONS table passes 'Current version' — story 184 extension,
 * D6), `onClose`. The dialog does not know where it was opened from.
 */
const IDLE = { loading: false, failed: false, errorText: '', report: {} }

const cellSx = { fontSize: 12, py: 0.5 }

/**
 * One version of the operation: its header `vN · Used in N workflows` and, under it, its usages or its explicit
 * empty. `selected` (RN-03.8): the version the row offers for dragging — the one N6 offered or the one chosen by
 * hand in `vN ▾` — takes a ring and the "Selected version" label; only one block can be it.
 */
const VersionBlock = ({ version, selected = false, selectedLabel = 'Selected version', t_ }) => {
  const number = version.versionNumber
  const usages = Array.isArray(version.usages) ? version.usages : []
  const header = versionLabelOf(number) + ' · ' + t_('Used in {{count}} workflows', { count: version.workflowCount || 0 })
  return (
    <Box
      data-testid={'operation-usages-version-' + number}
      data-selected={selected ? 'true' : 'false'}
      sx={(theme) => ({
        p: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : 'transparent'
      })}
    >
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: usages.length > 0 ? 0.5 : 0 }}>
        <Typography data-testid={'operation-usages-version-header-' + number} sx={{ fontSize: 13, fontWeight: 600 }}>{header}</Typography>
        {selected
          ? <Chip data-testid={'operation-usages-selected-' + number} size='small' color='primary' variant='outlined' label={t_(selectedLabel)} sx={{ height: 20, fontSize: 11 }} />
          : null}
      </Stack>
      {usages.length === 0
        ? <Typography data-testid={'operation-usages-version-empty-' + number} variant='caption' color='text.secondary'>{t_('Not used in any workflow')}</Typography>
        : (
          <Table size='small' data-testid={'operation-usages-table-' + number}>
            <TableHead>
              <TableRow>
                <TableCell sx={cellSx}>{t_('Code')}</TableCell>
                <TableCell sx={cellSx}>{t_('Name')}</TableCell>
                <TableCell sx={cellSx}>{t_('Version')}</TableCell>
                <TableCell sx={cellSx}>{t_('Latest version')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usages.map((usage, index) => (
                <TableRow key={(usage.nodeId || '') + '-' + index} data-testid={'operation-usage-row-' + number + '-' + index} draggable={false}>
                  <TableCell sx={cellSx}>{usage.workflowCode || ''}</TableCell>
                  <TableCell sx={cellSx}>{usage.workflowName || ''}</TableCell>
                  <TableCell sx={cellSx}>{versionLabelOf(usage.workflowVersion)}</TableCell>
                  <TableCell sx={cellSx}>{usage.isLatestWorkflowVersion ? t_('Latest version') : ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
    </Box>
  )
}

const OperationUsagesDialog = ({ open = false, code = '', name = '', selectedVersionNumber = 0, selectedVersionLabel = 'Selected version', onClose = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [state, setState] = useState(IDLE)
  const generationRef = useRef(0)

  useEffect(() => {
    if (!open || !code) {
      setState(IDLE)
      return
    }
    const generation = generationRef.current + 1
    generationRef.current = generation
    setState({ ...IDLE, loading: true })
    const load = async () => {
      try {
        const report = (await getOperationUsagesByCode(code)) || {}
        if (generationRef.current === generation) {
          setState({ ...IDLE, report })
        }
      } catch (thrown) {
        if (generationRef.current === generation) {
          // RN-03.11 / contrato-sun.md §4: a 400 or 404 WITH a business literal is shown translated (the literal is the
          // key); anything else — bodiless 403, network, 500 — says the usages could not be loaded (U5).
          const { status, error } = readOperationLibraryError(thrown)
          const literal = (status === 400 || status === 404) && error !== '' ? error : ''
          setState({ ...IDLE, failed: true, errorText: literal })
        }
      }
    }
    load()
  }, [open, code])

  const versions = Array.isArray(state.report.versions) ? state.report.versions : []
  const loaded = !state.loading && !state.failed && !!state.report.code
  // RN-03.10 (CA4): when NO version is used, one sentence instead of blocks of empties.
  const unused = loaded && versions.every((version) => !(Array.isArray(version.usages) && version.usages.length > 0))
  const title = code + (name ? ' · ' + name : '')
  return (
    <SmartDialog
      setOpen={open}
      cancel={false}
      accept={false}
      close
      // PO (user testing, 01/10/2026): the house header — icon centred over the title — with the operations glyph
      // the panel row and the Operations family already use.
      title={createDialogTitle({ title, icon: HandymanOutlinedIcon })}
      closeCallback={onClose}
      renderComponent={
        <Box data-testid='operation-usages-dialog' data-selected-version={selectedVersionNumber} sx={{ minWidth: 420 }}>
          {state.loading
            ? <Box data-testid='operation-usages-loading' sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
            : null}
          {state.failed
            ? <Typography data-testid='operation-usages-error' color='error' sx={{ py: 1 }}>{t_(state.errorText || 'The usages could not be loaded')}</Typography>
            : null}
          {unused
            ? <Typography data-testid='operation-usages-empty' color='text.secondary' sx={{ py: 1 }}>{t_('No version of this operation is used in any workflow')}</Typography>
            : null}
          {loaded && !unused
            ? (
              <Stack spacing={1}>
                {versions.map((version) => (
                  <VersionBlock
                    key={version.versionNumber}
                    version={version}
                    selected={Number(version.versionNumber) === Number(selectedVersionNumber)}
                    selectedLabel={selectedVersionLabel}
                    t_={t_}
                  />
                ))}
              </Stack>
              )
            : null}
        </Box>
      }
    />
  )
}

export default OperationUsagesDialog
